<template>
  <el-card
    class="process-config-card collapsible-card"
    :class="{ 'validation-error': props.tieneError }"
    shadow="hover"
    :body-style="{ padding: '6px' }"
  >
    <!-- Header de la card siempre visible -->
    <el-row
      :gutter="8"
      class="process-header-row"
    >
      <!-- Nombre del proceso con collapse -->
      <el-col
        :xs="24"
        :sm="4"
      >
        <div
          class="process-name-collapsible"
          @click="toggleCollapse"
        >
          <el-icon
            class="drag-handle"
            style="cursor: grab; margin-right: 8px; font-size: 16px"
            @click.stop
          >
            <Rank />
          </el-icon>

          <div style="display: flex; align-items: center; flex: 1">
            <el-icon
              class="collapse-icon"
              :class="{ expanded: !isColapsed }"
            >
              <ArrowDown />
            </el-icon>
            <span class="process-name-text">{{ proceso.Descripcion || '—' }}</span>
          </div>
        </div>
      </el-col>

      <!-- Selección de Sección -->
      <el-col
        :xs="24"
        :sm="5"
      >
        <CustomSelect
          :model-value="modelValue.Seccion"
          :span-column="24"
          :field-key="'Codigo'"
          :field-label="'Descripcion'"
          :label="'Sección'"
          :settings="{
            filterable: true,
            clearable: true,
            options: seccionesDisponibles,
            placeholder: 'Seleccionar',
            disabled: !modelValue.CodigoProceso,
            inputClass: props.tieneError && !modelValue.CodigoSeccion ? 'validation-error' : '',
          }"
          :store-object="true"
          @update:model-value="onSeccionSelectChanged"
        />
      </el-col>

      <!-- Switch Por defecto -->
      <el-col
        :xs="24"
        :sm="3"
      >
        <div class="switch-container">
          <div class="switch-label">Por defecto</div>
          <el-switch
            :model-value="modelValue.PorDefecto"
            size="small"
            @update:model-value="updatePorDefecto"
          />
        </div>
      </el-col>

      <!-- Switch Admite Suaje -->
      <el-col
        :xs="24"
        :sm="3"
      >
        <div class="switch-container">
          <div class="switch-label">Admite Suaje</div>
          <el-switch
            :model-value="modelValue.AdmiteSuaje"
            size="small"
            @update:model-value="updateAdmiteSuaje"
          />
        </div>
      </el-col>

      <!-- Selección de Recurso -->
      <el-col
        :xs="24"
        :sm="6"
      >
        <CustomSelect
          :model-value="getRecursoSeleccionado()"
          :span-column="24"
          :field-key="'Codigo'"
          :field-label="'Descripcion'"
          :label="'Recurso'"
          :settings="{
            filterable: true,
            clearable: true,
            options: recursosDisponibles,
            placeholder: 'Seleccionar',
          }"
          :store-object="true"
          @update:model-value="onRecursoSelectChanged"
        />
      </el-col>

      <!-- Botón de eliminar -->
      <el-col
        :xs="24"
        :sm="2"
        class="action-col"
      >
        <el-button
          circle
          size="small"
          type="danger"
          :icon="IconDelete"
          aria-label="Eliminar proceso"
          title="Eliminar proceso"
          @click="$emit('remove')"
        />
      </el-col>
    </el-row>

    <!-- Contenido colapsable - configuración del proceso -->
    <!-- <transition name="collapse"> -->
    <div
      v-show="!isColapsed"
      class="process-config-content"
    >
      <div class="config-main">
        <!-- Materiales del proceso (agrupados por categoría) -->
        <div
          v-if="modelValue.PorDefecto"
          class="materials-section"
        >
          <div class="materials-header">
            <div class="materials-label">Items por Categoría</div>
          </div>

          <el-alert
            v-if="!hasCategoriasForProc"
            title="Configura categorías en el Paso 1 para habilitar los items."
            type="info"
            :closable="false"
            class="materials-alert"
          />

          <!-- Materiales agrupados por categoría -->
          <div
            v-if="hasCategoriasForProc"
            class="materials-by-category"
          >
            <process-materials-by-category
              :model-value="modelValue.materialesPorCategoria"
              :proceso="modelValue"
              :categorias-del-proceso="categoriasDelProceso"
              :materiales-disponibles="materialesDisponibles"
              @update:model-value="updateMaterialesPorCategoria"
              @material-changed="$emit('material-changed', $event)"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- </transition> -->
  </el-card>
</template>

<script setup>
import {
  ref,
  computed,
} from 'vue';
import {
  Delete as IconDelete,
  ArrowDown,
  Rank,
} from '@element-plus/icons-vue';
import ProcessMaterialsByCategory from './process_materials_by_category.vue';
import CustomSelect from '@/shared/components/custom_select.vue';

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  proceso: {
    type: Object,
    required: true,
    validator: (value) => {
      return (
        value && typeof value.Codigo !== 'undefined' && typeof value.Descripcion !== 'undefined'
      );
    },
  },
  index: {
    type: Number,
    required: true,
  },
  seccionesDisponibles: {
    type: Array,
    default: () => [],
  },
  recursosDisponibles: {
    type: Array,
    default: () => [],
  },
  procesosConCategorias: {
    type: Array,
    default: () => [],
  },
  materialesDisponibles: {
    type: Map,
    default: () => new Map(),
  },
  tieneError: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['update:modelValue', 'remove', 'material-changed']);

// Estado local
const isColapsed = ref(true);

// Computed
const hasCategoriasForProc = computed(() => {
  const procesoConCategorias = props.procesosConCategorias.find(
    (p) =>
      p.CodigoProceso === props.modelValue.CodigoProceso ||
      p.Proceso?.Codigo === props.modelValue.CodigoProceso,
  );
  return procesoConCategorias?.categorias?.some((c) => !!c.CategoriaMaterial?.Codigo) || false;
});

const categoriasDelProceso = computed(() => {
  return (
    props.procesosConCategorias.find(
      (p) =>
        p.CodigoProceso === props.modelValue.CodigoProceso ||
        p.Proceso?.Codigo === props.modelValue.CodigoProceso,
    )?.categorias || []
  );
});

// Métodos utilitarios
const getSeccionSeleccionada = () => {
  if (!props.modelValue.CodigoSeccion) return null;
  return (
    props.seccionesDisponibles.find(
      (seccion) => seccion.Codigo === props.modelValue.CodigoSeccion,
    ) || null
  );
};

const getRecursoSeleccionado = () => {
  if (!props.modelValue.CodigoRecurso) return null;
  return (
    props.recursosDisponibles.find(
      (recurso) => recurso.Codigo === props.modelValue.CodigoRecurso,
    ) || null
  );
};

// Métodos de actualización
const updateSeccion = (nuevoValor) => {
  const procesoActualizado = { ...props.modelValue, CodigoSeccion: nuevoValor };
  emit('update:modelValue', procesoActualizado);
};

const onSeccionSelectChanged = (seccionSeleccionada) => {
  const nuevoCodigoSeccion =
    seccionSeleccionada && seccionSeleccionada.Codigo ? seccionSeleccionada.Codigo : null;

  // Actualizar tanto el código como el objeto completo de la sección
  const procesoActualizado = {
    ...props.modelValue,
    CodigoSeccion: nuevoCodigoSeccion,
    Seccion: seccionSeleccionada || null,
  };

  emit('update:modelValue', procesoActualizado);
};

const updatePorDefecto = (nuevoValor) => {
  const procesoActualizado = { ...props.modelValue, PorDefecto: nuevoValor };

  if (!nuevoValor) {
    if (procesoActualizado.materialesPorCategoria) {
      procesoActualizado.materialesPorCategoria = {};
    }

    if (procesoActualizado.codigosMateriales && procesoActualizado.codigosMateriales.length > 0) {
      procesoActualizado.codigosMateriales = [];
    }
  } else {
    // Si se activa "Por defecto", expandir el contenido
    isColapsed.value = false;
  }

  emit('update:modelValue', procesoActualizado);
};

const updateAdmiteSuaje = (nuevoValor) => {
  emit('update:modelValue', { ...props.modelValue, AdmiteSuaje: nuevoValor });
};

const updateRecurso = (nuevoValor) => {
  const procesoActualizado = { ...props.modelValue, CodigoRecurso: nuevoValor };
  emit('update:modelValue', procesoActualizado);
};

const onRecursoSelectChanged = (recursoSeleccionado) => {
  const nuevoCodigoRecurso =
    recursoSeleccionado && recursoSeleccionado.Codigo ? recursoSeleccionado.Codigo : null;
  updateRecurso(nuevoCodigoRecurso);
};

const updateMaterialesPorCategoria = (nuevoValor) => {
  const procesoActualizado = { ...props.modelValue, materialesPorCategoria: nuevoValor };
  emit('update:modelValue', procesoActualizado);
};

// Métodos de collapse
const toggleCollapse = () => {
  isColapsed.value = !isColapsed.value;
};

// Exponer métodos necesarios
defineExpose({
  collapse: () => {
    isColapsed.value = true;
  },
  expand: () => {
    isColapsed.value = false;
  },
  isCollapsed: () => isColapsed.value,
});
</script>

<style scoped>
.process-config-card {
  margin-bottom: 12px;
}

.process-config-card :deep(.el-card__body) {
  padding: 6px;
}

.action-col {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
}

.collapsible-card {
  overflow: hidden;
}

.process-header-row {
  align-items: flex-end;
  margin-bottom: 0;
}

.process-name-collapsible {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  padding: 8px 0;
  min-height: 32px;
}

.process-name-collapsible:hover {
  color: var(--el-color-primary);
}

.process-name-collapsible .collapse-icon {
  transition: transform 0.1s ease;
  transform: rotate(-90deg);
  color: var(--el-text-color-secondary);
}

.process-name-collapsible .collapse-icon.expanded {
  transform: rotate(0deg);
}

.process-name-text {
  font-weight: 600;
  font-size: 14px;
  color: var(--el-text-color-primary);
}

.switch-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
  min-height: 32px;
  align-items: flex-start;
}

.switch-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--el-text-color-regular);
  white-space: nowrap;
}

.process-config-content {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--el-border-color-lighter);
}

.config-main {
  flex: 1;
}

.materials-section {
  margin-top: 12px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.materials-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.materials-label {
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
}

.materials-alert {
  margin-bottom: 8px;
}

.materials-by-category {
  margin-top: 16px;
}

/* Transición para el collapse */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.1s ease;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 1000px;
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 768px) {
  .process-header-row .el-col {
    margin-bottom: 8px;
  }

  .process-header-row .action-col {
    text-align: center;
    margin-bottom: 0;
  }
}

/* Estilos para validación visual - específicos para este componente */
:deep(.validation-error .el-select__wrapper) {
  border: 2px solid #f56565 !important;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.2);
}

:deep(.validation-error:focus-within .el-select__wrapper) {
  animation: validation-pulse 1.5s infinite;
}

/* Efecto de pulse para campos con error */
@keyframes validation-pulse {
  0% {
    box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(245, 101, 101, 0.1);
  }
  100% {
    box-shadow: 0 0 0 2px rgba(245, 101, 101, 0.4);
  }
}
</style>
